import React, { useEffect, useMemo } from 'react'
import Prism from 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'
import DOMPurify from 'dompurify'
import type { PostDetail } from '../../types'
import { PostMeta } from './PostMeta'
import { TagBadge } from './TagBadge'
import { AsciiDivider } from '../terminal/AsciiDivider'
import { SubscribeForm } from '../newsletter/SubscribeForm'
import { useNavigate } from 'react-router-dom'
//import { useThemeStore } from '../../store/themeStore'

const CONTAINER = '.post-raw-content'

function transformCssForContainer(rawCss: string): string {
  const specialRules: string[] = []
  let remaining = rawCss

  // ── @media(prefers-color-scheme:dark) handling ─────────────────────────────
  // Must run BEFORE :root handler so nested :root blocks are not double-processed.
  // In our app, dark mode = html has data-theme="" (empty/absent), light = data-theme="light".
  // So the correct dark-mode selector is :root:not([data-theme="light"]).

  // Case 1: @media(prefers-color-scheme:dark) { :root { ... } }
  // → :root:not([data-theme="light"]) CONTAINER { ... }
  remaining = remaining.replace(
    /@media\s*\(\s*prefers-color-scheme\s*:\s*dark\s*\)\s*\{\s*:root\s*(\{[^}]*\})\s*\}/g,
    (_, block) => {
      specialRules.push(`:root:not([data-theme="light"]) ${CONTAINER} ${block}`)
      return ''
    }
  )

  // Case 2: @media(prefers-color-scheme:dark) { singleSelector { ... } } (no nested :root)
  // → :root:not([data-theme="light"]) CONTAINER singleSelector { ... }
  remaining = remaining.replace(
    /@media\s*\(\s*prefers-color-scheme\s*:\s*dark\s*\)\s*\{\s*([^{}]+?)\s*\{([^}]*)\}\s*\}/g,
    (_, selector, declarations) => {
      specialRules.push(`:root:not([data-theme="light"]) ${CONTAINER} ${selector.trim()} {${declarations}}`)
      return ''
    }
  )

  // :root { ... } → CONTAINER (CSS variables defined on container, not document root)
  remaining = remaining.replace(
    /:root\s*(\{[^}]*\})/g,
    (_, block) => {
      specialRules.push(`${CONTAINER} ${block}`)
      return ''
    }
  )

  // html.dark { ... } → :root:not([data-theme="light"]) CONTAINER { ... }
  // (In our app, dark mode = no data-theme="light" attribute on html element)
  remaining = remaining.replace(
    /html\.dark\s*(\{[^}]*\})/g,
    (_, block) => {
      specialRules.push(`:root:not([data-theme="light"]) ${CONTAINER} ${block}`)
      return ''
    }
  )

  // body { ... } → CONTAINER (body base styles applied to container element)
  remaining = remaining.replace(
    /\bbody\s*(\{[^}]*\})/g,
    (_, block) => {
      specialRules.push(`${CONTAINER} ${block}`)
      return ''
    }
  )

  const scopedBlock = `@scope (${CONTAINER}) {\n${remaining}\n}`
  return [...specialRules, scopedBlock].join('\n\n')
}

function parseRawHtml(html: string): {
  bodyContent: string
  transformedStyles: string
  scripts: string[]
} {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')

  // Extract inline scripts BEFORE DOMPurify strips them
  const scripts = Array.from(doc.querySelectorAll('script'))
    .map((el) => el.textContent ?? '')
    .filter(Boolean)

  // Extract all styles (from <head> and <body>)
  const rawStyles = Array.from(doc.querySelectorAll('style'))
    .map((el) => el.textContent ?? '')
    .join('\n')

  // Remove style and script elements from body before sanitizing
  doc.querySelectorAll('body style, body script').forEach((el) => el.remove())

  // Strip leading/trailing blank lines from <pre><code> blocks so that code panels
  // don't render with empty whitespace at the top or bottom of the block.
  doc.querySelectorAll('pre > code').forEach((code) => {
    const nodes = code.childNodes
    if (nodes.length === 0) return
    const first = nodes[0]
    if (first.nodeType === Node.TEXT_NODE && first.textContent) {
      first.textContent = first.textContent.replace(/^\n+/, '')
    }
    const last = nodes[nodes.length - 1]
    if (last.nodeType === Node.TEXT_NODE && last.textContent) {
      last.textContent = last.textContent.replace(/\n+$/, '')
    }
  })

  const bodyContent = DOMPurify.sanitize(doc.body.innerHTML, {
    ADD_TAGS: ['pre', 'code'],
    ADD_ATTR: [
      'class', 'style', 'tabindex',
      'data-tip-id', 'data-estep',
      'onclick', 'disabled',
      'id', 'href', 'role', 'aria-label',
      'language',
    ],
  })

  const transformedStyles = transformCssForContainer(rawStyles)

  return { bodyContent, transformedStyles, scripts }
}

// ── Theme bridge ──────────────────────────────────────────────────────────────
// Message type shared by parent ↔ iframe communication.
// const THEME_CHANNEL = 'CTB_THEME'

// /**
//  * Injected before </body> in every uploaded HTML post.
//  *
//  * Does two things:
//  *   1. Listens for CTB_THEME messages from the parent → sets data-theme on <html>
//  *   2. Watches data-theme via MutationObserver → relays local changes up to parent
//  *      (so the iframe's own toggle button also updates the main app's theme)
//  */
// const BRIDGE_SCRIPT = `<script>
// (function () {
//   var CH = 'CTB_THEME';

//   // Parent → iframe: apply theme
//   window.addEventListener('message', function (e) {
//     if (e.data && e.data.type === CH && typeof e.data.theme === 'string') {
//       document.documentElement.setAttribute('data-theme', e.data.theme);
//     }
//   });

//   // iframe → parent: relay any data-theme change (from iframe's own toggle or parent echo)
//   // MutationObserver only fires when the value actually changes, preventing echo loops.
//   new MutationObserver(function (mutations) {
//     mutations.forEach(function (m) {
//       if (m.attributeName === 'data-theme') {
//         window.parent.postMessage(
//           { type: CH, theme: document.documentElement.getAttribute('data-theme') },
//           '*'
//         );
//       }
//     });
//   }).observe(document.documentElement, {
//     attributes: true,
//     attributeFilter: ['data-theme'],
//   });
// })();
// <\/script>`

// /** Insert the bridge script just before </body>. Falls back to appending. */
// function injectBridge(html: string): string {
//   const MARKER = '</body>'
//   const idx = html.lastIndexOf(MARKER)
//   if (idx === -1) return html + BRIDGE_SCRIPT
//   return html.slice(0, idx) + BRIDGE_SCRIPT + html.slice(idx)
// }

// ── Component ─────────────────────────────────────────────────────────────────

interface PostViewerProps {
  post: PostDetail
}

export const PostViewer: React.FC<PostViewerProps> = ({ post }) => {
  const navigate = useNavigate()
  // const { theme, setTheme } = useThemeStore()
  // const iframeRef = useRef<HTMLIFrameElement>(null)

  // Parse raw HTML once per file change (avoid re-parse on re-render)
  const parsedRawHtml = useMemo(
    () => (post.rawHtmlFile ? parseRawHtml(post.rawHtmlFile) : null),
    [post.rawHtmlFile]
  )

  useEffect(() => {
    // Only run Prism on WYSIWYG posts; raw HTML posts handle their own highlighting
    if (!post.rawHtmlFile) Prism.highlightAll()
  }, [post.bodyHtml, post.rawHtmlFile])

  // Inject and execute scripts from raw HTML posts after content mounts.
  // Each script is wrapped in an IIFE so that const/let declarations are scoped
  // locally — preventing "already declared" errors when the user navigates between
  // posts that share the same variable names (e.g. EXP_TOTAL, expCurrent).
  // After the IIFE runs, any interactive-explainer functions the HTML's inline
  // onclick handlers reference (expNavigate, expGoTo) are re-exposed on window.
  useEffect(() => {
    if (!parsedRawHtml?.scripts?.length) return
    const injected: HTMLScriptElement[] = []
    parsedRawHtml.scripts.forEach((src) => {
      const el = document.createElement('script')
      // Clear the nav-dots container before each script runs so that if the post
      // HTML has multiple <script> blocks that each call buildExpNav(), the second
      // call starts from a clean slate instead of appending duplicate dots.
      el.textContent =
        `(function(){\n` +
        `var __sn=document.getElementById('expStepNav');if(__sn)__sn.innerHTML='';\n` +
        `${src}\n` +
        `if(typeof expNavigate==='function') window.expNavigate=expNavigate;\n` +
        `if(typeof expGoTo==='function') window.expGoTo=expGoTo;\n` +
        `})();`
      document.head.appendChild(el)
      injected.push(el)
    })
    return () => {
      injected.forEach((el) => el.parentNode?.removeChild(el))
    }
  }, [parsedRawHtml])

  // Remove the <div class="divider"> that immediately precedes .explainer-wrap.
  // Post HTML files include both a divider separator AND .explainer-wrap already
  // has border-top — two visible horizontal lines. Drop the redundant divider.
  useEffect(() => {
    if (!post.rawHtmlFile) return
    const container = document.querySelector('.post-raw-content')
    if (!container) return
    container.querySelectorAll<HTMLElement>('.explainer-wrap').forEach((wrap) => {
      const prev = wrap.previousElementSibling
      if (prev instanceof HTMLElement && prev.classList.contains('divider')) {
        prev.remove()
      }
    })
  }, [post.rawHtmlFile, parsedRawHtml])

  // Collapse image containers when their <img> fails to load (e.g. relative paths
  // in post HTML that resolve incorrectly when embedded in the React app).
  useEffect(() => {
    if (!post.rawHtmlFile) return
    const container = document.querySelector('.post-raw-content')
    if (!container) return

    const handlers: Array<{ img: HTMLImageElement; fn: () => void }> = []

    container.querySelectorAll<HTMLImageElement>('img').forEach((img) => {
      const collapse = () => {
        img.style.display = 'none'
        // If the parent element exists solely to frame this image, shrink it too
        const parent = img.parentElement
        if (
          parent &&
          parent !== container &&
          parent.children.length === 1 &&
          (parent.textContent ?? '').trim() === ''
        ) {
          parent.style.setProperty('width', '0', 'important')
          parent.style.setProperty('min-width', '0', 'important')
          parent.style.setProperty('height', '0', 'important')
          parent.style.setProperty('min-height', '0', 'important')
          parent.style.setProperty('overflow', 'hidden', 'important')
          parent.style.setProperty('border', 'none', 'important')
          parent.style.setProperty('padding', '0', 'important')
          parent.style.setProperty('margin', '0', 'important')
        }
      }

      // Already broken (naturalWidth === 0 and complete === true means load failed)
      if (img.complete && img.naturalWidth === 0) {
        collapse()
      } else {
        img.addEventListener('error', collapse)
        handlers.push({ img, fn: collapse })
      }
    })

    return () => {
      handlers.forEach(({ img, fn }) => img.removeEventListener('error', fn))
    }
  }, [post.rawHtmlFile, parsedRawHtml])

  // ── Send theme to iframe whenever the main app theme changes ─────────────────
  // useEffect(() => {
  //   if (!post.rawHtmlFile) return
  //   iframeRef.current?.contentWindow?.postMessage(
  //     { type: THEME_CHANNEL, theme },
  //     '*'
  //   )
  // }, [theme, post.rawHtmlFile])

  // // ── Listen for theme changes relayed FROM the iframe ─────────────────────────
  // useEffect(() => {
  //   if (!post.rawHtmlFile) return

  //   const handler = (e: MessageEvent) => {
  //     // Only accept messages from our iframe, not arbitrary origins
  //     if (e.source !== iframeRef.current?.contentWindow) return
  //     if (e.data?.type === THEME_CHANNEL && (e.data.theme === 'dark' || e.data.theme === 'light')) {
  //       // Calling setTheme when already the same value is a no-op in Zustand
  //       setTheme(e.data.theme)
  //     }
  //   }

  //   window.addEventListener('message', handler)
  //   return () => window.removeEventListener('message', handler)
  // }, [post.rawHtmlFile, setTheme])

  const safeHtml = post.rawHtmlFile
    ? ''
    : DOMPurify.sanitize(post.bodyHtml, {
        ADD_TAGS: ['pre', 'code'],
        ADD_ATTR: ['class', 'language'],
      })

  const handleTagClick = (tag: string) => {
    navigate(`/tags/${tag}`)
  }

  // ── Uploaded HTML files: render in DOM so search engines can index the content ──
  if (post.rawHtmlFile) {
    const { bodyContent, transformedStyles } = parsedRawHtml!
    return (
      <>
        <style>{`
          .post-raw-wrap {
            max-width: 900px;
            margin: 0 auto;
            padding: 24px 0;
          }
          .post-raw-meta {
            margin-bottom: 16px;
          }
          .post-raw-tags {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-bottom: 16px;
          }
          .post-raw-content {
            width: 100%;
            display: block;
          }

          /* ── Override main-app global styles so they don't bleed into post content ── */

          /* Headings: main app sets JetBrains Mono globally; posts use sans-serif */
          .post-raw-content h1,
          .post-raw-content h2,
          .post-raw-content h3,
          .post-raw-content h4,
          .post-raw-content h5,
          .post-raw-content h6 {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            color: var(--text, #1a1a18);
          }

          /* Links: post uses --blue not main app's --accent-blue */
          .post-raw-content a {
            color: var(--blue, #185FA5);
          }

          /* Buttons: posts use sans-serif, not monospace */
          .post-raw-content button {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          }

          /* Inline code: override main app colors with post's own vars */
          .post-raw-content :not(pre) > code {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            background: var(--bg3, #eceae4);
            color: var(--text, #1a1a18);
            border: none;
            padding: 2px 6px;
            border-radius: 4px;
          }

          /* Pre blocks: override main app's larger padding */
          .post-raw-content pre {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            background: var(--bg3, #eceae4);
            border: 0.5px solid var(--border, rgba(0,0,0,0.12));
            padding: 16px 18px;
            line-height: 1.9;
          }

          /* Tables: ensure th doesn't use main app's monospace */
          .post-raw-content th {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            background: var(--bg2, #f5f4f0);
            color: var(--text3, #9a9a94);
          }
          .post-raw-content td {
            color: var(--text2, #5a5a56);
          }

          /* Issue 3 fix: add visible bottom border to table wrappers */
          .post-raw-content .compare-wrap {
            border-bottom: 0.5px solid var(--border, rgba(0,0,0,0.12));
          }

          /* Issue 1 fix: ensure side-by-side panels are equal height */
          .post-raw-content .side-by,
          .post-raw-content .exp-compare {
            align-items: stretch;
          }
          .post-raw-content .sbc,
          .post-raw-content .exp-cmp {
            display: flex;
            flex-direction: column;
          }
          .post-raw-content .sbc-body,
          .post-raw-content .exp-cmp-body {
            flex: 1;
          }

          /* Paragraphs and general text */
          .post-raw-content p {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          }
        `}</style>
        {transformedStyles && (
          <style>{transformedStyles}</style>
        )}
        <div className="post-raw-wrap">
          <div className="post-raw-meta">
            <PostMeta
              author={post.author}
              publishedAt={post.publishedAt}
              readingTimeMin={post.readingTimeMin}
              category={post.category}
              updatedAt={post.updatedAt}
              showReadingTime={false}
            />
          </div>
          {post.tags.length > 0 && (
            <div className="post-raw-tags">
              {post.tags.map((tag) => (
                <TagBadge key={tag} tag={tag} onClick={handleTagClick} />
              ))}
            </div>
          )}
          <article
            className="post-raw-content"
            dangerouslySetInnerHTML={{ __html: bodyContent }}
          />
          <SubscribeForm variant="inline" />
        </div>
      </>
    )
  }

  return (
    <>
      <style>{`
        .post-viewer {
          max-width: 780px;
          margin: 0 auto;
          padding: 32px 0;
        }
        .post-viewer-title {
          font-size: clamp(1.4rem, 3vw, 2.1rem);
          font-weight: 700;
          line-height: 1.2;
          color: var(--text-primary);
          margin-bottom: 16px;
        }
        .post-viewer-meta {
          margin-bottom: 12px;
        }
        .post-viewer-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 8px;
        }
        .post-viewer-article {
          margin-top: 8px;
          line-height: 1.8;
          font-size: 0.97rem;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
        }
        .post-viewer-article h2 {
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 1.35rem;
          margin: 2.2rem 0 0.75rem;
          color: var(--accent-blue);
          border-bottom: 0.5px solid var(--border-color-solid);
          padding-bottom: 6px;
        }
        .post-viewer-article h3 {
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 1.1rem;
          margin: 1.75rem 0 0.5rem;
          color: var(--accent-teal);
        }
        .post-viewer-article h4 {
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.95rem;
          margin: 1.25rem 0 0.4rem;
          color: var(--text-primary);
          letter-spacing: 0.02em;
        }
        .post-viewer-article p {
          margin: 1rem 0;
          color: var(--text-primary);
          line-height: 1.8;
        }
        .post-viewer-article a {
          color: var(--accent-blue);
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        .post-viewer-article a:hover {
          color: var(--accent-teal);
        }
        .post-viewer-article pre {
          background-color: var(--code-bg);
          border: 0.5px solid var(--border-color-solid);
          border-radius: var(--radius-md, 8px);
          padding: 1.25rem 1.5rem;
          overflow-x: auto;
          margin: 1.5rem 0;
          font-size: 0.85rem;
          line-height: 1.65;
        }
        .post-viewer-article code {
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.84em;
          background-color: var(--code-bg);
          color: var(--accent-teal);
          padding: 0.15em 0.4em;
          border-radius: var(--radius-sm, 4px);
          border: 0.5px solid var(--border-color-solid);
        }
        .post-viewer-article pre code {
          background: none;
          border: none;
          padding: 0;
          color: inherit;
          font-size: inherit;
        }
        .post-viewer-article blockquote {
          border-left: 2px solid var(--accent-amber);
          padding: 0.75rem 1.25rem;
          margin: 1.5rem 0;
          background-color: rgba(250, 199, 117, 0.05);
          color: var(--text-secondary);
          font-style: italic;
          border-radius: 0 var(--radius-sm, 4px) var(--radius-sm, 4px) 0;
        }
        .post-viewer-article table {
          width: 100%;
          border-collapse: collapse;
          margin: 1.5rem 0;
          font-size: 0.875rem;
          overflow-x: auto;
          display: block;
        }
        .post-viewer-article th,
        .post-viewer-article td {
          border: 0.5px solid var(--border-color-solid);
          padding: 8px 12px;
          text-align: left;
        }
        .post-viewer-article th {
          background-color: var(--bg-tertiary);
          color: var(--text-primary);
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.8em;
          font-weight: 600;
          letter-spacing: 0.03em;
        }
        .post-viewer-article td {
          color: var(--text-secondary);
        }
        .post-viewer-article ul,
        .post-viewer-article ol {
          padding-left: 1.5rem;
          margin: 0.75rem 0;
        }
        .post-viewer-article li {
          margin: 0.35rem 0;
          line-height: 1.75;
        }
        .post-viewer-article img {
          max-width: 100%;
          height: auto;
          border-radius: var(--radius-md, 8px);
          margin: 1.25rem 0;
          border: 0.5px solid var(--border-color-solid);
        }
        .post-viewer-article hr {
          border: none;
          border-top: 0.5px solid var(--border-color-solid);
          margin: 2rem 0;
        }
        /* Inline highlight from search */
        .post-viewer-article em {
          background-color: rgba(250, 199, 117, 0.2);
          color: var(--accent-amber);
          font-style: normal;
          padding: 0 2px;
          border-radius: 2px;
        }
      `}</style>
      <div className="post-viewer">
        <h1 className="post-viewer-title">{post.title}</h1>
        <div className="post-viewer-meta">
          <PostMeta
            author={post.author}
            publishedAt={post.publishedAt}
            readingTimeMin={post.readingTimeMin}
            category={post.category}
            updatedAt={post.updatedAt}
          />
        </div>
        {post.tags.length > 0 && (
          <div className="post-viewer-tags">
            {post.tags.map((tag) => (
              <TagBadge key={tag} tag={tag} onClick={handleTagClick} />
            ))}
          </div>
        )}
        <AsciiDivider width={60} />
        <article
          className="post-viewer-article"
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />
        <SubscribeForm variant="inline" />
      </div>
    </>
  )
}
